import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Static fallback sections shown while loading or if API returns nothing
const STATIC_SECTIONS = [
  {
    title: 'Search',
    links: [
      { label: 'All Plants', to: '/search' },
      { label: 'Statement Plants', to: '/search' },
      { label: 'Easy Care', to: '/search' },
      { label: 'Trailing Vines', to: '/search' },
      { label: 'Succulents', to: '/search' },
      { label: 'Flowering Plants', to: '/search' },
    ],
  },
  {
    title: 'Help',
    links: [
      { label: 'Plant Care Guide' },
      { label: 'Delivery Info' },
      { label: 'Returns' },
      { label: 'Plant Quiz' },
      { label: 'FAQs' },
      { label: 'Contact Us', to: '/contact' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About Naturiq', to: '/about' },
      { label: 'Blog', to: '/blog' },
      { label: 'Sustainability', to: '/about#sustainability' },
      { label: 'Careers' },
      { label: 'Press' },
    ],
  },
];

function FooterCol({ section }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="footer__col">
      <button
        className="footer__col-toggle"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <h4 className="footer__col-title">{section.title}</h4>
        <span className={`footer__col-chevron${open ? ' footer__col-chevron--open' : ''}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>
      <ul className={`footer__links${open ? ' footer__links--open' : ''}`}>
        {section.links.map(l => (
          <li key={l.label}>
            {l.to
              ? l.external
                ? <a href={l.to} target="_blank" rel="noopener noreferrer">{l.label}</a>
                : <Link to={l.to}>{l.label}</Link>
              : <a href="#!">{l.label}</a>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function HelpBanner() {
  return (
    <div className="footer__help">
      <div className="footer__help-inner">
        <h2 className="footer__help-heading">
          Need help to choose plants?<br />We're here to help
        </h2>
        <a href="#!" className="footer__help-cta">
          Chat With Us
        </a>
      </div>
    </div>
  );
}

const DEFAULT_SETTINGS = { socialLinks: { instagram: '', facebook: '', linkedin: '', x: '' }, copyrightText: 'Naturiq. All rights reserved.' };

export default function Footer() {
  const [sections, setSections] = useState(STATIC_SECTIONS);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    fetch(`${BASE}/api/footer-items`)
      .then(r => r.ok ? r.json() : null)
      .then(items => {
        if (!items || items.length === 0) return; // keep static fallback
        // Group by column, preserving server order within each column
        const map = {};
        items.forEach(item => {
          if (!map[item.column]) map[item.column] = [];
          map[item.column].push({ label: item.label, to: item.href, external: item.external });
        });
        const dynamic = Object.keys(map).map(col => ({ title: col, links: map[col] }));
        setSections(dynamic);
      })
      .catch(() => {}); // silently keep static fallback on error

    fetch(`${BASE}/api/footer-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return; // keep defaults
        setSettings({
          socialLinks: { ...DEFAULT_SETTINGS.socialLinks, ...(data.socialLinks || {}) },
          copyrightText: data.copyrightText || DEFAULT_SETTINGS.copyrightText,
        });
      })
      .catch(() => {}); // silently keep defaults on error
  }, []);

  const year = new Date().getFullYear();
  const social = settings.socialLinks;

  return (
    <footer className="footer">
      <HelpBanner />
      <div className="footer__inner">
        {/* Brand */}
        <div className="footer__brand">
          <div className="footer__logo">
            <img src="/Naturiq_logo_white.png" alt="Naturiq" className="footer__logo-img" />
          </div>
          <p className="footer__tagline">
            
          </p>
          <div className="footer__socials">
            <a href={social.instagram || 'https://instagram.com/naturiq.in'} className="footer__social" aria-label="Instagram" target={social.instagram ? '_blank' : undefined} rel={social.instagram ? 'noopener noreferrer' : undefined}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/>
                <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
              </svg>
            </a>
            <a href={social.facebook || 'https://facebook.com/naturiq.in'} className="footer__social" aria-label="Facebook" target={social.facebook ? '_blank' : undefined} rel={social.facebook ? 'noopener noreferrer' : undefined}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
            </a>
            <a href={social.linkedin || 'https://linkedin.com/company/naturiq-in'} className="footer__social" aria-label="LinkedIn" target={social.linkedin ? '_blank' : undefined} rel={social.linkedin ? 'noopener noreferrer' : undefined}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                <rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
              </svg>
            </a>
            <a href={social.x || 'https://twitter.com/Naturiq_in'} className="footer__social" aria-label="X (Twitter)" target={social.x ? '_blank' : undefined} rel={social.x ? 'noopener noreferrer' : undefined}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Link columns — accordion on mobile */}
        {sections.map(section => (
          <FooterCol key={section.title} section={section} />
        ))}
      </div>

      <div className="footer__bottom">
        <p>© {year} {settings.copyrightText}</p>
      </div>
    </footer>
  );
}